// Insights Workshop copy of frontend/src/playground/PitchBoard.tsx, ported from the Insights Lab at
// 12749ec1 (2026-10-08) and repointed at /api/v1/workshop. Not shared with the
// Lab on purpose (see src/workshop/InsightsWorkshop.tsx); fix both if a fix
// applies to both.
/**
 * The Pitch Board — one pitch, left to right like every pitch in the
 * product (ours attacking right), that stays visible while different
 * analytics results are layered onto it.
 *
 * Every layer is a SNAPSHOT of a query result (columns, rows, and the
 * mapping it was drawn with), pinned from a canvas card. Layers stack in
 * the order they were added; the legend names each one, carries its color
 * (click the swatch to cycle), and can hide or remove it. The board draws
 * with the same pure-SVG grammar as the chart layer — no new dependencies.
 */
import { type PitchLayer } from '../lib/workshopApi'
import {
  DEFAULT_PITCH, PitchLines, convexHull, fmt, idx, num, palette, pitchGeom,
  regionClip, str, zoneRectsFor, type Cell, type Column, type PitchOptions,
} from './charts'

/** The next palette color no current layer is using. */
export function nextLayerColor(layers: PitchLayer[]): string {
  const p = palette()
  const used = new Set(layers.map((l) => l.color))
  return p.find((c) => !used.has(c)) ?? p[layers.length % p.length]!
}

/** Cycle a layer's color to the next palette slot. */
export function cycleColor(color: string): string {
  const p = palette()
  const at = p.indexOf(color)
  return p[(at + 1) % p.length]!
}

type G = ReturnType<typeof pitchGeom>

function pts(layer: PitchLayer, g: G): { px: [number, number]; m: [number, number]; label: string }[] {
  const cols = layer.columns as Column[]
  const xi = idx(cols, layer.mapping.x), yi = idx(cols, layer.mapping.y)
  const li = idx(cols, layer.mapping.label)
  if (xi < 0 || yi < 0) return []
  const out: { px: [number, number]; m: [number, number]; label: string }[] = []
  for (const r of layer.rows as Cell[][]) {
    const x = num(r[xi]!), y = num(r[yi]!)
    if (x === null || y === null) continue
    out.push({
      px: g.toPx(x, y) as unknown as [number, number],
      m: g.toMetres(x, y) as unknown as [number, number],
      label: li >= 0 ? str(r[li]!) : '',
    })
  }
  return out
}

function PointsLayer({ layer, g, dots = true }: { layer: PitchLayer; g: G; dots?: boolean }) {
  const p = pts(layer, g)
  const isShape = layer.kind === 'shape'
  const hull = isShape ? convexHull(p.map((q) => q.px)) : []
  return (
    <g>
      {hull.length >= 3 && (
        <polygon points={hull.map((q) => q.join(',')).join(' ')}
                 fill={layer.color} fillOpacity={0.12}
                 stroke={layer.color} strokeWidth={1.5} />
      )}
      {dots && p.map((q, i) => (
        <g key={i}>
          <circle cx={q.px[0]} cy={q.px[1]} r={isShape ? 6 : 3.5}
                  fill={layer.color} fillOpacity={isShape ? 0.9 : 0.6} />
          {isShape && q.label && (
            <text x={q.px[0]} y={q.px[1] + 3} textAnchor="middle"
                  className="pg-shape-dot-label">{q.label}</text>
          )}
        </g>
      ))}
    </g>
  )
}

/** Pixel rect for a region label on the board — the same left-to-right
 *  frame as `regionClip` itself: x from the left goal line, y lateral. */
function boardClip(region: string | undefined, g: G):
  { px: number; py: number; pw: number; ph: number } | null {
  if (!region) return null
  const c = regionClip(region)
  if (!c) return null
  return {
    px: g.P + c.x0 * g.S,
    py: g.P + c.y0 * g.S,
    pw: (c.x1 - c.x0) * g.S,
    ph: (c.y1 - c.y0) * g.S,
  }
}

function HeatLayer({ layer, g, inverse }: { layer: PitchLayer; g: G; inverse: boolean }) {
  // When the layer carries a region, bin only within that slice.
  const clip = regionClip(layer.region ?? '')
  const ax0 = clip ? clip.x0 : 0, ax1 = clip ? clip.x1 : g.L
  const ay0 = clip ? clip.y0 : 0, ay1 = clip ? clip.y1 : g.Wd
  const aw = ax1 - ax0, ah = ay1 - ay0
  const NA = clip ? Math.max(4, Math.round(12 * (aw / g.L))) : 12
  const NL = clip ? Math.max(4, Math.round(8 * (ah / g.Wd))) : 8
  const bins = new Array<number>(NA * NL).fill(0)
  let n = 0
  for (const q of pts(layer, g)) {
    if (q.m[0] < ax0 || q.m[0] >= ax1 || q.m[1] < ay0 || q.m[1] >= ay1) continue
    const a = Math.min(NA - 1, Math.floor((q.m[0] - ax0) / aw * NA))
    const l = Math.min(NL - 1, Math.floor((q.m[1] - ay0) / ah * NL))
    bins[a * NL + l] = (bins[a * NL + l] ?? 0) + 1
    n += 1
  }
  if (!n) return null
  const max = Math.max(...bins)
  const cw = ah / NL, ch = aw / NA
  // Few points: show dots instead of cells.
  if (n <= 40) {
    const all = pts(layer, g).filter(
      (q) => q.m[0] >= ax0 && q.m[0] < ax1 && q.m[1] >= ay0 && q.m[1] < ay1)
    return (
      <g>
        {all.map((q, i) => (
          <circle key={i} cx={q.px[0]} cy={q.px[1]} r={4}
                  fill={layer.color} fillOpacity={0.75} />
        ))}
      </g>
    )
  }
  return (
    <g>
      {bins.map((c, i) => {
        const a = Math.floor(i / NL), l = i % NL
        const frac = max === 0 ? 0 : c / max
        const alpha = 0.55 * (inverse ? 1 - frac : frac)
        if (alpha <= 0.03) return null
        // Left to right like every pitch: attack axis along x, lateral y.
        const bx = g.P + (ax0 + a * ch) * g.S
        const by = g.P + (ay0 + l * cw) * g.S
        return (
          <rect key={i} x={bx} y={by}
                width={ch * g.S} height={cw * g.S}
                fill={layer.color} fillOpacity={alpha} />
        )
      })}
    </g>
  )
}

function FlowLayer({ layer, g }: { layer: PitchLayer; g: G }) {
  const cols = layer.columns as Column[]
  const xi = idx(cols, layer.mapping.x), yi = idx(cols, layer.mapping.y)
  const x2i = idx(cols, layer.mapping.x2), y2i = idx(cols, layer.mapping.y2)
  if (xi < 0 || yi < 0 || x2i < 0 || y2i < 0) return null
  const segs: [number, number, number, number][] = []
  for (const r of layer.rows as Cell[][]) {
    const x = num(r[xi]!), y = num(r[yi]!), x2 = num(r[x2i]!), y2 = num(r[y2i]!)
    if (x === null || y === null || x2 === null || y2 === null) continue
    const [ax, ay] = g.toPx(x, y), [bx, by] = g.toPx(x2, y2)
    segs.push([ax, ay, bx, by])
  }
  return (
    <g>
      {segs.map((s, i) => (
        <g key={i}>
          <line x1={s[0]} y1={s[1]} x2={s[2]} y2={s[3]}
                stroke={layer.color} strokeWidth={1.2} strokeOpacity={0.5} />
          <circle cx={s[2]} cy={s[3]} r={2.2} fill={layer.color} fillOpacity={0.7} />
        </g>
      ))}
    </g>
  )
}

function PassNetLayer({ layer, g }: { layer: PitchLayer; g: G }) {
  const cols = layer.columns as Column[]
  const fx = idx(cols, layer.mapping.x), fy = idx(cols, layer.mapping.y)
  const tx = idx(cols, layer.mapping.x2), ty = idx(cols, layer.mapping.y2)
  const ni = idx(cols, layer.mapping.size)
  if (fx < 0 || fy < 0 || tx < 0 || ty < 0) return null
  const edges: { a: [number, number]; b: [number, number]; n: number }[] = []
  for (const r of layer.rows as Cell[][]) {
    const x1 = num(r[fx]!), y1 = num(r[fy]!), x2 = num(r[tx]!), y2 = num(r[ty]!)
    if (x1 === null || y1 === null || x2 === null || y2 === null) continue
    edges.push({
      a: g.toPx(x1, y1) as unknown as [number, number],
      b: g.toPx(x2, y2) as unknown as [number, number],
      n: ni >= 0 ? (num(r[ni]!) ?? 1) : 1,
    })
  }
  if (!edges.length) return null
  const maxN = Math.max(...edges.map((e) => e.n))
  return (
    <g>
      {edges.map((e, i) => (
        <line key={i} x1={e.a[0]} y1={e.a[1]} x2={e.b[0]} y2={e.b[1]}
              stroke={layer.color} strokeOpacity={0.55}
              strokeWidth={0.8 + 4.2 * (e.n / maxN)} />
      ))}
      {edges.map((e, i) => (
        <circle key={`n${i}`} cx={e.a[0]} cy={e.a[1]} r={4}
                fill={layer.color} fillOpacity={0.9} />
      ))}
    </g>
  )
}

function ZonesLayer({ layer, g }: { layer: PitchLayer; g: G }) {
  const cols = layer.columns as Column[]
  const xi = idx(cols, layer.mapping.x), yi = idx(cols, layer.mapping.y)
  if (xi < 0) return null
  const rows = layer.rows as Cell[][]
  const rects = zoneRectsFor(g.L, g.Wd, rows.map((r) => str(r[xi]!)))
  const matched: { rect: [number, number, number, number]; val: number | null }[] = []
  for (const r of rows) {
    const rect = rects[str(r[xi]!).trim().toLowerCase().replace(/_/g, ' ')]
    if (rect) matched.push({ rect, val: yi >= 0 ? num(r[yi]!) : null })
  }
  const vals = matched.map((m) => m.val).filter((v): v is number => v !== null)
  const lo = Math.min(...(vals.length ? vals : [0]))
  const hi = Math.max(...(vals.length ? vals : [1]))
  return (
    <g>
      {matched.map((m, i) => {
        const [x0, x1, y0, y1] = m.rect
        const alpha = m.val === null || hi === lo
          ? 0.3 : 0.1 + 0.5 * ((m.val - lo) / (hi - lo))
        // The zone rects are authored for the horizontal pitch (x along
        // the attack axis) — which the board now is; no transpose.
        return (
          <g key={i}>
            <rect x={g.P + x0 * g.S} y={g.P + y0 * g.S}
                  width={(x1 - x0) * g.S} height={(y1 - y0) * g.S}
                  fill={layer.color} fillOpacity={alpha} />
            {m.val !== null && (
              <text x={g.P + ((x0 + x1) / 2) * g.S}
                    y={g.P + ((y0 + y1) / 2) * g.S}
                    textAnchor="middle" className="pg-zone-value">{fmt(m.val)}</text>
            )}
          </g>
        )
      })}
    </g>
  )
}

function LayerArt({ layer, g }: { layer: PitchLayer; g: G }) {
  switch (layer.kind) {
    case 'heatmap': return <HeatLayer layer={layer} g={g} inverse={false} />
    case 'spacemap': return <HeatLayer layer={layer} g={g} inverse={true} />
    case 'flow': return <FlowLayer layer={layer} g={g} />
    case 'pitchzones': return <ZonesLayer layer={layer} g={g} />
    case 'passnet': return <PassNetLayer layer={layer} g={g} />
    case 'shape': return <PointsLayer layer={layer} g={g} />
    default: return <PointsLayer layer={layer} g={g} />
  }
}

export default function PitchBoard({ layers, attack, onToggle, onRemove, onRecolor }: {
  layers: PitchLayer[]
  /** Club-named direction captions ("SKC attacking" right, "LAG" left) —
   *  absent when the game's identity is unresolved, and the board then
   *  claims no direction. */
  attack?: PitchOptions['attack']
  onToggle: (id: string) => void
  onRemove: (id: string) => void
  onRecolor: (id: string) => void
}) {
  const g = pitchGeom(DEFAULT_PITCH)
  const visible = layers.filter((l) => l.visible)
  return (
    <div className="cv-board">
      <svg viewBox={`0 0 ${g.vw} ${g.vh}`} role="img" aria-label="Pitch board">
        <rect x={g.P} y={g.P} width={g.L * g.S} height={g.Wd * g.S}
              fill="var(--color-surface-sunken)" />
        {visible.map((l) => <LayerArt key={l.id} layer={l} g={g} />)}
        <PitchLines g={g} />
        {/* Region outlines: a dashed rect showing which slice of the pitch
            each region-filtered layer covers. */}
        {visible.map((l) => {
          const vc = boardClip(l.region, g)
          if (!vc) return null
          return (
            <rect key={`rgn-${l.id}`} x={vc.px} y={vc.py}
                  width={vc.pw} height={vc.ph}
                  fill="none" stroke={l.color} strokeWidth={2}
                  strokeDasharray="6 3" strokeOpacity={0.7} />
          )
        })}
        {attack && (
          <>
            <text x={g.P + g.L * g.S - 4} y={g.P + 14} textAnchor="end"
                  className="pg-shape-measure">{attack.rightward} &#8594;</text>
            {attack.leftward && (
              <text x={g.P + 4} y={g.P + 14} textAnchor="start"
                    className="pg-shape-measure">&#8592; {attack.leftward}</text>
            )}
          </>
        )}
      </svg>
      {layers.length === 0 ? (
        <p className="pg-note">Pin a spatial result here with a card&rsquo;s
          {' '}<strong>+ Pitch</strong> button.</p>
      ) : (
        <ul className="cv-board__legend">
          {layers.map((l) => (
            <li key={l.id} className={l.visible ? '' : 'is-hidden'}>
              <button className="cv-board__swatch" style={{ background: l.color }}
                      onClick={() => onRecolor(l.id)} title="Change color" />
              <span className="cv-board__label" title={l.label}>{l.label}</span>
              <button className="ac-btn-sm" onClick={() => onToggle(l.id)}
                      title={l.visible ? 'Hide' : 'Show'}>
                {l.visible ? '\u25C9' : '\u25CB'}
              </button>
              <button className="ac-btn-sm" onClick={() => onRemove(l.id)}
                      title="Remove from pitch">&times;</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
